import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CybersourceService } from '../../services/cybersource.service';
import { OnlinePaymentService } from '../../services/online-payment.service';
import { PaymentEventsService } from '../../services/payment-events.service';
import { DataService } from '../../services/data.service';
import { AlertDialogComponent } from '../../shared/alert-dialog/alert-dialog.component';
import { MastercardPayload } from '../../models/payment.model';
import { PayAtTableNiPayload } from '../../models/pay-at-table.model';

@Component({
  selector: 'app-cybersource',
  templateUrl: './cybersource.component.html',
  styleUrls: ['./cybersource.component.scss'],
  standalone: false
})
export class CybersourceComponent implements OnInit, OnDestroy {

  public loading = true;
  // Two containers: the button list and the card form both render inline.
  public readonly buttonsContainerId = 'cybersource-payment-buttons';
  public readonly screenContainerId = 'cybersource-payment-screen';

  private payload: MastercardPayload | PayAtTableNiPayload | null = null;
  private transactionId: string | null = null;

  constructor(
    private cybersourceService: CybersourceService,
    private onlinePaymentService: OnlinePaymentService,
    private paymentEventsService: PaymentEventsService,
    private dataService: DataService,
    private dialog: MatDialog,
    private router: Router,
    private location: Location,
  ) { }

  public ngOnInit(): void {
    const payload = this.onlinePaymentService.consumePendingPayload();

    if (!payload) {
      this.location.back();
      return;
    }

    this.payload = payload;
    this.startSession();
  }

  private startSession(): void {
    if (!this.payload) {
      return;
    }

    this.loading = true;
    this.cybersourceService.getSession(this.payload).subscribe({
      next: (session) => {
        this.transactionId = session.transaction_id;
        this.cybersourceService.mount(
          { paymentSelection: this.buttonsContainerId, paymentScreen: this.screenContainerId },
          session,
          {
            onToken: (transientToken) => this.handleToken(transientToken),
            onDeclined: () => this.handleDeclinedPayment(),
            onFatalError: () => this.handleFatalError(),
          },
        ).then((mounted) => {
          // Resolves once the widget is attached, not once the guest has paid —
          // so it is safe to drop the overlay here.
          this.loading = false;
          if (!mounted) {
            // The SDK script itself failed to load, or the capture context
            // carried no library URL — retrying in place would fail the same way.
            this.handleFatalError();
          }
        });
      },
      // The capture context couldn't be created (backend/network down, or the
      // venue's credentials are wrong) — retrying immediately would loop.
      error: () => this.handleFatalError(),
    });
  }

  public ngOnDestroy(): void {
    this.cybersourceService.unmount();
  }

  // Bound to the top-bar's customBack input.
  public cancel = (): void => {
    this.paymentEventsService.notifyPaymentCancelled();
    this.location.back();
  };

  private handleToken(transientToken: string): void {
    if (!this.transactionId) {
      // Mount succeeded but our own session state is missing — an internal bug,
      // not a card decline. Don't retry into the same broken state.
      this.handleFatalError();
      return;
    }

    // The token is worth nothing until the server authorizes it. Keep the
    // spinner up: money is moving and the guest must not be able to leave or
    // submit twice while it does.
    this.loading = true;

    this.cybersourceService.validateCybersourceSession({
      transaction_id: this.transactionId,
      transient_token: transientToken,
    }).subscribe((validated) => {
      this.loading = false;

      if (!validated) {
        // Either declined or something worse; either way nothing settled, and
        // the transient token is single-use so the guest needs a fresh widget.
        this.handleDeclinedPayment();
        return;
      }

      this.dataService.transactionLoaded.set(false);
      this.dialog.closeAll();
      this.router.navigate(['/bill-paid'], {
        queryParams: { table_id: this.dataService.table()?.id, transaction_id: this.transactionId },
      });
    });
  }

  private handleDeclinedPayment(): void {
    this.paymentEventsService.notifyPaymentCancelled();
    AlertDialogComponent.open(this.dialog, {
      type: 'error', title: 'PAYMENT.TITLE', message: 'PAYMENT.ERROR', enableCancelBtn: false,
    });
    // A transient token is single-use, so a retry needs a brand-new capture
    // context — remount from scratch rather than reusing the old session.
    this.cybersourceService.unmount();
    this.startSession();
  }

  private handleFatalError(): void {
    this.paymentEventsService.notifyPaymentCancelled();
    AlertDialogComponent.open(this.dialog, {
      type: 'error', title: 'PAYMENT.TITLE', message: 'PAYMENT.ERROR', enableCancelBtn: false,
    });
    this.cybersourceService.unmount();
    this.location.back();
  }

}
